import { Link, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useMemo, useState } from "react";
import {
  Clock,
  AlertCircle,
  Upload,
  Inbox,
  Loader2,
  Star,
  CalendarDays,
  ChevronRight,
  Bell,
  FileText,
  CheckCircle2,
  Circle,
  ClipboardList,
  CheckCheck,
  Building2,
  MessageSquare,
} from "lucide-react";

import { useProviderUser } from "@/hooks/useProviderUser";
import {
  differenceInDays,
  formatDistanceToNow,
  parse,
  parseISO,
  addDays,
  format,
  startOfDay,
  endOfDay,
  isWithinInterval,
  isToday,
  isTomorrow,
} from "date-fns";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/ajax/queryClient";
import { useMutation, useSuspenseQuery, useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { DOCUMENT_CATEGORIES } from "@/consts/document_categories";
import { ExpirationBadgeTime } from "@/features/documents/components/Badge/ExpirationBadgeTime";
import { PushNotificationService } from "@/common/utils/PushNotificationService";
import { PushNotificationsPrompt } from "@/components/PushNotifications/PushNotificationsPrompt";
import { getProviderNotificationAudience } from "@/components/PushNotifications/notification-audience";
import {
  JobOfferCard,
  JobOfferCardProps,
} from "@/features/job-offer/matching/job-offer-card";
import { isJobInPast } from "@/features/jobs/isJobInPast";
import { HiringProcessChecklistDialog } from "@/features/hiring-process/hiring-process-checklist-dialog";
import { useTRPC } from "@/trpc";
import { trpc } from "@/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ProposalScheduleChanges } from "@/features/job-offer/components/proposal-schedule-changes";
import { toast } from "sonner";

// Helper function to get job schedule for the next N days
interface DailyScheduleJob {
  id: string;
  title: string;
  facility?: {
    id: string;
    name: string;
  } | null;
  startTime: string;
  endTime: string;
}

interface DailyScheduleEntry {
  date: Date;
  jobs: DailyScheduleJob[];
}

function getJobScheduleForNextNDays(
  upcomingJobs: {
    id: string;
    title: string | null;
    facility: {
      id: string;
      name: string;
    } | null;
    shifts?: {
      id: string;
      startsAt: Date | string;
      endsAt: Date | string;
    }[];
  }[],
  numberOfDays: number,
): DailyScheduleEntry[] {
  const today = startOfDay(new Date());
  const schedule: DailyScheduleEntry[] = [];

  for (let i = 0; i < numberOfDays; i++) {
    const currentDate = addDays(today, i);
    const jobsOnThisDay: DailyScheduleJob[] = [];

    for (const job of upcomingJobs) {
      if (job.shifts && job.shifts.length > 0) {
        for (const shift of job.shifts) {
          const shiftStartDate =
            typeof shift.startsAt === "string"
              ? parseISO(shift.startsAt)
              : shift.startsAt;
          const shiftEndDate =
            typeof shift.endsAt === "string"
              ? parseISO(shift.endsAt)
              : shift.endsAt;

          const interval = {
            start: startOfDay(shiftStartDate),
            end: endOfDay(shiftEndDate),
          };
          if (isWithinInterval(currentDate, interval)) {
            const startTimeStr = format(shiftStartDate, "HH:mm");
            const endTimeStr = format(shiftEndDate, "HH:mm");

            jobsOnThisDay.push({
              id: job.id,
              title: job.title || "Untitled Job",
              facility: job.facility
                ? { id: job.facility.id, name: job.facility.name }
                : null,
              startTime: startTimeStr,
              endTime: endTimeStr,
            });
            break;
          }
        }
      }
    }
    schedule.push({ date: currentDate, jobs: jobsOnThisDay });
  }
  return schedule;
}

export const Route = createFileRoute("/_provider/provider/home/")({
  loader: async () => {
    return Promise.all([
      queryClient.ensureQueryData(trpc.provider.getProviderUser.queryOptions()),
      queryClient.ensureQueryData(
        trpc.credentialFiles.getFiles.queryOptions(
          {
            filterId: undefined,
            switchUserId: undefined,
          },
          {
            retry: 2,
          },
        ),
      ),
      queryClient.ensureQueryData(
        trpc.jobOffer.providerListAccepted.queryOptions(),
      ),
      queryClient.ensureQueryData(
        trpc.jobOffer.providerListOpen.queryOptions(),
      ),
      queryClient.ensureQueryData(
        trpc.timeEntry.getShiftsNeedingEntries.queryOptions(),
      ),
      queryClient.ensureQueryData(
        trpc.jobOffer.providerListPendingProposals.queryOptions(),
      ),
      queryClient.ensureQueryData(
        trpc.organizationBusinessUnitAffiliation.listForProvider.queryOptions(),
      ),
    ]);
  },
  component: HomePage,
});

const DOCUMENT_ALERT_COOKIE_NAME = "documentsAlertDismissed";
const NOTIFICATIONS_ALERT_COOKIE_NAME = "notificationsDismissed";

function HomePage() {
  const providerUser = useProviderUser();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const filesQuery = useSuspenseQuery(
    trpc.credentialFiles.getFiles.queryOptions(
      {
        filterId: undefined,
        switchUserId: undefined,
      },
      {
        retry: 2,
      },
    ),
  );
  const filesData = filesQuery.data;

  const { data: acceptedJobsData, isLoading: isLoadingAcceptedJobs } = useQuery(
    trpc.jobOffer.providerListAccepted.queryOptions(),
  );
  const { data: openJobOffersData, isLoading: isLoadingOpenJobs } = useQuery(
    trpc.jobOffer.providerListOpen.queryOptions(),
  );
  const { data: timesheetData, isLoading: isLoadingTimesheets } = useQuery(
    trpc.timeEntry.getShiftsNeedingEntries.queryOptions(),
  );
  const { data: pendingProposalsData } = useQuery(
    trpc.jobOffer.providerListPendingProposals.queryOptions(),
  );

  const { data: hiringInstancesData } = useQuery(
    trpc.hiringProcess.getMyProcesses.queryOptions(),
  );

  const affiliationsQuery = useQuery(
    trpc.organizationBusinessUnitAffiliation.listForProvider.queryOptions(),
  );
  const [chatBusinessUnitId, setChatBusinessUnitId] = useState<string | null>(
    null,
  );
  const ensureBusinessUnitChat = useMutation(
    trpc.chat.providerEnsureBusinessUnitThread.mutationOptions({
      onMutate: ({ organizationBusinessUnitId }) => {
        setChatBusinessUnitId(organizationBusinessUnitId);
      },
      onSuccess: async (thread) => {
        await queryClient.invalidateQueries({
          queryKey: ["providerChatThreadsInfinite"],
        });
        navigate({
          to: "/provider/chat",
          search: { thread: thread.id },
        });
      },
      onError: (error) => {
        toast.error(error.message || "Unable to start this conversation.");
      },
      onSettled: () => {
        setChatBusinessUnitId(null);
      },
    }),
  );

  // State for proposal job offer dialog
  const [selectedProposalJobOfferId, setSelectedProposalJobOfferId] = useState<
    string | null
  >(null);
  const { data: selectedProposalJobOffer } = useQuery(
    trpc.jobOffer.getForCard.queryOptions(
      { id: selectedProposalJobOfferId ?? "" },
      { enabled: !!selectedProposalJobOfferId },
    ),
  );

  const [checklistJobPostId, setChecklistJobPostId] = useState<string | null>(
    null,
  );

  const [showNotificationsCard, setShotNotificationsCard] = useState<
    boolean | null
  >(null);

  // State for Job Detail Modal
  const [isJobDetailModalOpen, setIsJobDetailModalOpen] = useState(false);
  const [selectedJobForModal, setSelectedJobForModal] = useState<
    null | JobOfferCardProps["jobOffer"]
  >(null);

  useEffect(() => {
    const checkPushStatus = async () => {
      const notificationsDismissed =
        Cookies.get(NOTIFICATIONS_ALERT_COOKIE_NAME) === "true";
      if (notificationsDismissed) {
        setShotNotificationsCard(false);
        return;
      }

      const status = await PushNotificationService.checkPermissionStatus();
      if (status) {
        setShotNotificationsCard(status.receive !== "granted");

        if (status.receive === "granted") {
          PushNotificationService.requestPermission();
        }
      }
    };
    checkPushStatus();
  }, []);

  // Calculate the missing required documents
  const missingDocuments = useMemo(
    () =>
      DOCUMENT_CATEGORIES.filter((document) => {
        return (
          !filesData.some(
            (f) =>
              f.type_id != null && document.fileTypeIds.includes(f.type_id),
          ) && !document.optional
        );
      }),
    [filesData],
  );

  const [showDocumentsAlert, setShowDocumentsAlert] = useState(false);

  useEffect(() => {
    const alertDismissed = Cookies.get(DOCUMENT_ALERT_COOKIE_NAME);
    if (alertDismissed !== "true") {
      setShowDocumentsAlert(true);
    }
  }, []);

  const dismissDocumentAlert = () => {
    Cookies.set(DOCUMENT_ALERT_COOKIE_NAME, "true", { expires: 30 });
    setShowDocumentsAlert(false);
  };

  const dismissNotificationsCard = () => {
    Cookies.set(NOTIFICATIONS_ALERT_COOKIE_NAME, "true", { expires: 30 });
    setShotNotificationsCard(false);
  };

  const upcomingAcceptedJobs = useMemo(() => {
    if (!acceptedJobsData) {
      return [];
    }
    return acceptedJobsData.filter((job) => !isJobInPast(job));
  }, [acceptedJobsData]);

  const jobScheduleForNext7Days = useMemo(() => {
    if (!upcomingAcceptedJobs) {
      return [];
    }
    return getJobScheduleForNextNDays(upcomingAcceptedJobs, 7).filter(
      (day) => day.jobs.length > 0,
    );
  }, [upcomingAcceptedJobs]);

  const openJobOffers = useMemo(() => {
    if (!openJobOffersData) {
      return [];
    }
    return openJobOffersData;
  }, [openJobOffersData]);

  const activeHiringInstances = useMemo(() => {
    if (!hiringInstancesData) {
      return [];
    }
    const doneStatuses = new Set(["completed", "skipped", "not_applicable"]);
    return hiringInstancesData.filter((instance) => {
      const providerItems = instance.items.filter(
        (item) => item.assigneeRole === "provider",
      );
      if (providerItems.length === 0) {
        return false;
      }
      return providerItems.some((item) => !doneStatuses.has(item.status));
    });
  }, [hiringInstancesData]);

  const expiringFiles = filesData.filter((file) => {
    if (!file.expires_at) {
      return false;
    }
    const daysUntilExpire = differenceInDays(
      parse(file.expires_at, "yyyy-MM-dd", new Date()),
      new Date(),
    );
    return daysUntilExpire <= 60;
  });

  const totalTimesheetsNeeded = useMemo(() => {
    if (!timesheetData) {
      return 0;
    }
    return timesheetData.reduce((sum, g) => sum + g.totalShifts, 0);
  }, [timesheetData]);

  const isLoading =
    isLoadingAcceptedJobs || isLoadingOpenJobs || isLoadingTimesheets;
  const hasTimesheetsNeeded = totalTimesheetsNeeded > 0;
  const hasOpenOffers = openJobOffers.length > 0;
  const hasUpcomingJobs = upcomingAcceptedJobs.length > 0;
  const hasExpiringDocs = expiringFiles.length > 0;
  const hasMissingDocs =
    providerUser?.jobPostsEnabled !== false &&
    showDocumentsAlert &&
    missingDocuments.length > 0;

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return "Good morning";
    }
    if (hour < 17) {
      return "Good afternoon";
    }
    return "Good evening";
  };

  // Determine action items for "Needs Your Attention" section
  const hasProposals = pendingProposalsData && pendingProposalsData.length > 0;
  const hasChecklist = activeHiringInstances.length > 0;
  const hasAttentionItems =
    hasOpenOffers || hasProposals || hasChecklist || hasTimesheetsNeeded;

  // Find the next/hero shift (today or tomorrow)
  const heroScheduleEntry =
    jobScheduleForNext7Days.find(
      ({ date }) => isToday(date) || isTomorrow(date),
    ) ?? null;
  const remainingScheduleDays = jobScheduleForNext7Days
    .filter(({ date }) => !isToday(date) && !isTomorrow(date))
    .slice(0, 5);

  const showCredentialsSection = hasExpiringDocs || hasMissingDocs;

  // Providers without job posts enabled (roster-only) don't browse for work,
  // so an empty schedule is just noise. Only show the schedule for them when
  // they actually have an active job offer — an open offer or an upcoming
  // accepted shift.
  const showScheduleSection =
    providerUser?.jobPostsEnabled !== false || hasOpenOffers || hasUpcomingJobs;

  if (!providerUser) {
    return;
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl w-full mx-auto space-y-6">
      {/* Page Header */}
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-gray-900">
          {getGreeting()}, {providerUser.firstName || "there"}
        </h1>
        <p className="text-sm text-gray-400 mt-0.5">
          {format(new Date(), "EEEE, MMMM d")}
        </p>
      </div>

      {providerUser.jobPostsEnabled === false && (
        <section className="animate-fade-in space-y-3">
          <h2 className="px-1 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Your Organizations
          </h2>
          {affiliationsQuery.isLoading ? (
            <div className="flex items-center justify-center rounded-xl border bg-white p-6 shadow-sm">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            </div>
          ) : affiliationsQuery.isError ? (
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-600">
                We couldn&apos;t load your organizations.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void affiliationsQuery.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : (affiliationsQuery.data?.length ?? 0) > 0 ? (
            <div className="overflow-hidden rounded-xl border bg-white shadow-sm divide-y">
              {affiliationsQuery.data?.map((affiliation) => (
                <div
                  key={affiliation.id}
                  className="flex items-center justify-between gap-4 p-4"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="rounded-lg bg-blue-50 p-2.5">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900">
                        {affiliation.businessUnitName}
                      </p>
                      <p className="truncate text-sm text-gray-500">
                        {affiliation.organizationName}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5"
                    disabled={ensureBusinessUnitChat.isPending}
                    onClick={() =>
                      ensureBusinessUnitChat.mutate({
                        organizationBusinessUnitId:
                          affiliation.organizationBusinessUnitId,
                      })
                    }
                  >
                    {chatBusinessUnitId ===
                      affiliation.organizationBusinessUnitId &&
                    ensureBusinessUnitChat.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MessageSquare className="h-4 w-4" />
                    )}
                    Message
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border bg-white p-4 text-sm text-gray-500 shadow-sm">
              No active organizations were found.
            </div>
          )}
        </section>
      )}

      {isLoading && (
        <div className="bg-white p-8 rounded-xl border flex items-center justify-center">
          <Loader2 className="h-8 w-8 mr-3 animate-spin text-blue-600" />
          <p className="text-lg text-gray-700">Loading...</p>
        </div>
      )}

      {!isLoading && (
        <>
          {/* Section 1: Needs Your Attention */}
          <div className="animate-fade-in space-y-1">
            <div className="flex items-center gap-2 px-1 mb-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Needs Your Attention
              </h2>
              {hasAttentionItems && (
                <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-red-100 text-red-600 text-xs font-semibold">
                  {[
                    hasOpenOffers ? openJobOffers.length : 0,
                    hasProposals ? pendingProposalsData!.length : 0,
                    hasChecklist ? activeHiringInstances.length : 0,
                    hasTimesheetsNeeded ? totalTimesheetsNeeded : 0,
                  ].reduce((a, b) => a + b, 0)}
                </span>
              )}
            </div>

            {hasAttentionItems ? (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden divide-y">
                {/* Open Job Offers */}
                {hasOpenOffers && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-blue-100 rounded-lg">
                          <Bell className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            Open Job Offers
                          </p>
                          <p className="text-xs text-blue-600">
                            {openJobOffers.length} offer
                            {openJobOffers.length !== 1 ? "s" : ""} awaiting
                            your response
                          </p>
                        </div>
                      </div>
                      <Link to="/provider/jobs/active">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 px-2"
                        >
                          View All
                          <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                        </Button>
                      </Link>
                    </div>
                    <div className="space-y-2">
                      {openJobOffers.slice(0, 2).map((job) => (
                        <div
                          key={job.id}
                          className="rounded-lg overflow-hidden border border-gray-100"
                        >
                          <JobOfferCard jobOffer={job} compact />
                        </div>
                      ))}
                      {openJobOffers.length > 2 && (
                        <Link to="/provider/jobs/active">
                          <button
                            type="button"
                            className="w-full text-center text-xs text-blue-600 hover:text-blue-700 py-1.5 font-medium"
                          >
                            +{openJobOffers.length - 2} more offer
                            {openJobOffers.length - 2 !== 1 ? "s" : ""}
                          </button>
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {/* Pending Proposals */}
                {hasProposals && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-amber-100 rounded-lg">
                          <CalendarDays className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            Pending Proposals
                          </p>
                          <p className="text-xs text-amber-600">
                            {pendingProposalsData!.length} proposal
                            {pendingProposalsData!.length !== 1 ? "s" : ""}{" "}
                            awaiting review
                          </p>
                        </div>
                      </div>
                      <Link to="/provider/chat">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 px-2"
                        >
                          View Chat
                          <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                        </Button>
                      </Link>
                    </div>
                    <div className="space-y-2">
                      {pendingProposalsData!.slice(0, 3).map((proposal) => (
                        <button
                          type="button"
                          key={proposal.id}
                          onClick={() =>
                            setSelectedProposalJobOfferId(proposal.jobOfferId)
                          }
                          className="w-full text-left rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors p-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">
                                {proposal.facilityName || "Unknown Facility"}
                              </p>
                              {proposal.jobOfferTitle && (
                                <p className="text-xs text-gray-500 truncate">
                                  {proposal.jobOfferTitle}
                                </p>
                              )}
                              {proposal.initiatedNote && (
                                <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">
                                  {proposal.initiatedNote}
                                </p>
                              )}
                              <ProposalScheduleChanges
                                schedulesToAdd={proposal.schedulesToAdd}
                                shiftsToCancel={proposal.shiftsToCancel}
                              />
                            </div>
                            <p className="text-xs text-gray-400 shrink-0 mt-0.5">
                              {formatDistanceToNow(
                                new Date(proposal.initiatedAt),
                                { addSuffix: true },
                              )}
                            </p>
                          </div>
                        </button>
                      ))}
                      {pendingProposalsData!.length > 3 && (
                        <Link to="/provider/chat">
                          <button
                            type="button"
                            className="w-full text-center text-xs text-amber-600 hover:text-amber-700 py-1.5 font-medium"
                          >
                            +{pendingProposalsData!.length - 3} more proposal
                            {pendingProposalsData!.length - 3 !== 1 ? "s" : ""}
                          </button>
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {/* Application Checklist */}
                {hasChecklist && (
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-1.5 bg-amber-100 rounded-lg">
                        <ClipboardList className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          Application Checklist
                        </p>
                        <p className="text-xs text-amber-600">
                          {activeHiringInstances.length} application
                          {activeHiringInstances.length !== 1 ? "s" : ""} with
                          items to complete
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {activeHiringInstances.map((instance) => {
                        const providerItems = instance.items.filter(
                          (item) => item.assigneeRole === "provider",
                        );
                        const doneItems = providerItems.filter((item) =>
                          (
                            [
                              "completed",
                              "skipped",
                              "not_applicable",
                            ] as string[]
                          ).includes(item.status),
                        );
                        const allDone =
                          doneItems.length === providerItems.length;
                        const pendingCount =
                          providerItems.length - doneItems.length;
                        return (
                          <button
                            key={instance.id}
                            type="button"
                            onClick={() =>
                              setChecklistJobPostId(instance.jobPostId)
                            }
                            className="w-full text-left rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors p-3 flex items-center justify-between gap-3"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              {allDone ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                              ) : (
                                <Circle className="h-4 w-4 text-amber-400 shrink-0" />
                              )}
                              <div className="min-w-0">
                                <span className="font-medium text-gray-900 text-sm truncate block">
                                  {instance.jobPost?.title ?? "Untitled Job"}
                                </span>
                                <span
                                  className={cn(
                                    "text-xs font-medium",
                                    allDone
                                      ? "text-green-600"
                                      : "text-amber-600",
                                  )}
                                >
                                  {allDone
                                    ? "All items complete"
                                    : `${doneItems.length}/${providerItems.length} done — ${pendingCount} remaining`}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Timesheets Due */}
                {hasTimesheetsNeeded && (
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-amber-100 rounded-lg">
                          <FileText className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            Timesheets Due
                          </p>
                          <p className="text-xs text-amber-600">
                            {totalTimesheetsNeeded} shift
                            {totalTimesheetsNeeded !== 1 ? "s" : ""} need time
                            entry submission
                          </p>
                        </div>
                      </div>
                      <Link to="/provider/jobs/timesheets">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 px-2"
                        >
                          Submit
                          <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* All caught up zero-state */
              <div className="bg-white rounded-xl border shadow-sm p-6 flex items-center gap-4">
                <div className="p-2.5 bg-green-100 rounded-full shrink-0">
                  <CheckCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    You're all caught up
                  </p>
                  <p className="text-sm text-gray-500">
                    No action items at the moment.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Your Schedule */}
          {showScheduleSection && (
            <div className="animate-fade-in space-y-1">
              <div className="flex items-center justify-between px-1 mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Your Schedule
                </h2>
                <Link to="/provider/jobs/active">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 px-2 text-xs"
                  >
                    View Calendar
                    <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                </Link>
              </div>

              {hasUpcomingJobs ? (
                <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                  {/* Hero shift — today or tomorrow */}
                  {heroScheduleEntry && (
                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b">
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "w-12 h-12 rounded-lg flex flex-col items-center justify-center text-center shrink-0",
                            isToday(heroScheduleEntry.date)
                              ? "bg-emerald-600 text-white"
                              : "bg-blue-600 text-white",
                          )}
                        >
                          <span className="text-xs font-medium uppercase leading-none">
                            {format(heroScheduleEntry.date, "MMM")}
                          </span>
                          <span className="text-lg font-bold leading-none mt-0.5">
                            {format(heroScheduleEntry.date, "d")}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-semibold text-gray-900">
                              {format(heroScheduleEntry.date, "EEEE")}
                            </p>
                            <span
                              className={cn(
                                "text-xs font-medium px-2 py-0.5 rounded-full",
                                isToday(heroScheduleEntry.date)
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-blue-100 text-blue-700",
                              )}
                            >
                              {isToday(heroScheduleEntry.date)
                                ? "Today"
                                : "Tomorrow"}
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            {heroScheduleEntry.jobs.map((scheduledJob) => {
                              const fullJob = upcomingAcceptedJobs.find(
                                (uj) => uj.id === scheduledJob.id,
                              );
                              return (
                                <button
                                  key={scheduledJob.id}
                                  type="button"
                                  className="w-full text-left rounded-lg bg-white border border-gray-100 px-3 py-2 hover:bg-gray-50 transition-colors"
                                  onClick={() => {
                                    if (fullJob) {
                                      setSelectedJobForModal(fullJob);
                                      setIsJobDetailModalOpen(true);
                                    }
                                  }}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className="w-1 h-8 bg-emerald-500 rounded-full shrink-0" />
                                      <div className="min-w-0">
                                        <p className="font-medium text-gray-900 text-sm truncate">
                                          {scheduledJob.facility?.name ||
                                            "Unknown Facility"}
                                        </p>
                                        {scheduledJob.title && (
                                          <p className="text-xs text-gray-500 truncate">
                                            {scheduledJob.title}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <p className="text-sm font-medium text-gray-900">
                                        {format(
                                          parseISO(
                                            `2000-01-01T${scheduledJob.startTime}`,
                                          ),
                                          "h:mm a",
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        to{" "}
                                        {format(
                                          parseISO(
                                            `2000-01-01T${scheduledJob.endTime}`,
                                          ),
                                          "h:mm a",
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Remaining schedule days */}
                  {remainingScheduleDays.length > 0 && (
                    <div className="divide-y">
                      {remainingScheduleDays.map(({ date, jobs }) => (
                        <div key={date.toISOString()} className="p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex flex-col items-center justify-center text-center shrink-0">
                              <span className="text-xs font-medium text-gray-500 uppercase leading-none">
                                {format(date, "MMM")}
                              </span>
                              <span className="text-sm font-bold text-gray-700 leading-none mt-0.5">
                                {format(date, "d")}
                              </span>
                            </div>
                            <p className="font-medium text-gray-700 text-sm">
                              {format(date, "EEEE")}
                            </p>
                          </div>
                          <div className="space-y-1.5 ml-13">
                            {jobs.map((scheduledJob) => {
                              const fullJob = upcomingAcceptedJobs.find(
                                (uj) => uj.id === scheduledJob.id,
                              );
                              return (
                                <button
                                  key={scheduledJob.id}
                                  type="button"
                                  className="w-full text-left p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                  onClick={() => {
                                    if (fullJob) {
                                      setSelectedJobForModal(fullJob);
                                      setIsJobDetailModalOpen(true);
                                    }
                                  }}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className="w-1 h-7 bg-emerald-400 rounded-full shrink-0" />
                                      <div className="min-w-0">
                                        <p className="font-medium text-gray-900 text-sm truncate">
                                          {scheduledJob.facility?.name ||
                                            "Unknown Facility"}
                                        </p>
                                        {scheduledJob.title && (
                                          <p className="text-xs text-gray-500 truncate">
                                            {scheduledJob.title}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <p className="text-xs font-medium text-gray-700">
                                        {format(
                                          parseISO(
                                            `2000-01-01T${scheduledJob.startTime}`,
                                          ),
                                          "h:mm a",
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        to{" "}
                                        {format(
                                          parseISO(
                                            `2000-01-01T${scheduledJob.endTime}`,
                                          ),
                                          "h:mm a",
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No shifts in upcoming days but there are accepted jobs */}
                  {jobScheduleForNext7Days.length === 0 && (
                    <div className="p-6 text-center text-gray-500 text-sm">
                      No shifts scheduled in the next 7 days
                    </div>
                  )}
                </div>
              ) : (
                /* Empty state — no jobs and no open offers */
                <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
                  <div className="w-14 h-14 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Inbox className="h-7 w-7 text-gray-400" />
                  </div>
                  <h3 className="font-semibold text-gray-700 mb-1">
                    No shifts scheduled
                  </h3>
                  <p className="text-sm text-gray-500 mb-5 max-w-xs mx-auto">
                    {providerUser.jobPostsEnabled
                      ? "Browse recommended jobs to find new opportunities."
                      : "You don't have any upcoming shifts scheduled."}
                  </p>
                  {providerUser.jobPostsEnabled && (
                    <Link to="/provider/jobs/recommended">
                      <Button variant="default" size="sm">
                        <Star className="h-4 w-4 mr-1.5" />
                        Find Jobs
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Section 3: Credentials (only if there is something to show) */}
          {showCredentialsSection && (
            <div className="animate-fade-in space-y-1">
              <div className="px-1 mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Credentials
                </h2>
              </div>

              <div className="bg-white rounded-xl border shadow-sm overflow-hidden divide-y">
                {/* Expiring documents */}
                {hasExpiringDocs && (
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-amber-100 rounded-lg shrink-0 mt-0.5">
                        <Clock className="h-4 w-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">
                              Expiring Documents
                            </p>
                            <p className="text-xs text-amber-600">
                              {expiringFiles.length} document
                              {expiringFiles.length !== 1 ? "s" : ""} expiring
                              soon
                            </p>
                          </div>
                          <Link to="/provider/documents">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 px-2 text-xs shrink-0"
                            >
                              Manage
                              <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                            </Button>
                          </Link>
                        </div>
                        <div className="space-y-1.5">
                          {expiringFiles.slice(0, 3).map((file) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2 gap-2"
                            >
                              <span className="font-medium text-gray-800 text-sm truncate">
                                {file.generated_title || file.file_name}
                              </span>
                              <ExpirationBadgeTime
                                expiresAt={file.expires_at!}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Missing required documents */}
                {hasMissingDocs && (
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-orange-100 rounded-lg shrink-0 mt-0.5">
                        <AlertCircle className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">
                          Missing Required Documents
                        </p>
                        <p className="text-xs text-orange-600 mt-0.5">
                          Upload these to start accepting jobs
                        </p>
                        <ul className="mt-2.5 space-y-1.5">
                          {missingDocuments.map((doc, index) => (
                            <li
                              key={index}
                              className="flex items-center text-sm text-gray-700"
                            >
                              <Upload className="h-3.5 w-3.5 mr-2 text-gray-400 shrink-0" />
                              {doc.name}
                            </li>
                          ))}
                        </ul>
                        <div className="flex items-center gap-2 mt-3">
                          <Link to="/provider/documents">
                            <Button
                              variant="default"
                              size="sm"
                              className="h-8 text-xs"
                            >
                              Upload Documents
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={dismissDocumentAlert}
                          >
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Push Notifications Banner */}
          {showNotificationsCard && (
            <div className="animate-fade-in bg-white p-5 rounded-xl border shadow-sm">
              <PushNotificationsPrompt
                onComplete={dismissNotificationsCard}
                audience={getProviderNotificationAudience(
                  providerUser.jobPostsEnabled,
                )}
                showHeading
              />
            </div>
          )}
        </>
      )}

      {/* Job Detail Modal */}
      <Dialog
        open={isJobDetailModalOpen}
        onOpenChange={setIsJobDetailModalOpen}
      >
        <DialogContent
          className="w-[calc(100%-1rem)] max-w-lg mx-auto max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-xl border-0 shadow-2xl"
          showCloseButton={false}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Job Details</DialogTitle>
          </DialogHeader>
          {selectedJobForModal && (
            <JobOfferCard
              jobOffer={selectedJobForModal}
              inDialog
              onClose={() => setIsJobDetailModalOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Proposal Job Offer Dialog */}
      <Dialog
        open={!!selectedProposalJobOfferId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedProposalJobOfferId(null);
          }
        }}
      >
        <DialogContent
          className="w-[calc(100%-1rem)] max-w-lg mx-auto max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-xl border-0 shadow-2xl"
          showCloseButton={false}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Job Offer Details</DialogTitle>
          </DialogHeader>
          {selectedProposalJobOffer && (
            <JobOfferCard
              jobOffer={selectedProposalJobOffer}
              inDialog
              onClose={() => setSelectedProposalJobOfferId(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {checklistJobPostId && (
        <HiringProcessChecklistDialog
          jobPostId={checklistJobPostId}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setChecklistJobPostId(null);
            }
          }}
        />
      )}
    </div>
  );
}
